import { getAuthHeader } from "./auth";

/**
 * Posts a Team Log message through the server (`POST /api/messages`) so the
 * shared sequence counter (`relay_counters/workspace`) is only ever changed
 * by the server (SEC-COUNTERS, spec #51075). The browser used to run its own
 * counter transaction, which forced the Firestore rule to let every signed-in
 * user write the counter.
 *
 * The server stamps `sequence`, `timestamp` and `workspace_id`, and applies
 * the same author/roster/directive gates as every other post — including the
 * rule that a human caller's `author` must be their own identity.
 */
export async function postWithSequence(
  _db: unknown,
  payload: {
    author: string;
    content: string;
    tags?: string[];
    workspace_id?: string;
    origin?: string;
    [key: string]: unknown;
  }
): Promise<{ id: string; sequence: number; projectTagWarning?: string }> {
  const { author, content, tags, origin, workspace_id } = payload;
  const headers: Record<string, string> = {
    ...(await getAuthHeader(workspace_id)),
    "Content-Type": "application/json",
  };
  const res = await fetch("/api/messages", {
    method: "POST",
    headers,
    body: JSON.stringify({
      author,
      content,
      tags,
      origin,
      replyToSequence: typeof payload.replyToSequence === "number" ? payload.replyToSequence : undefined,
    }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.message ?? body?.error ?? "";
    } catch {
      // non-JSON error body — fall through to the status code
    }
    throw new Error(detail || `Post failed (${res.status})`);
  }
  const data = (await res.json()) as { id: string; sequence: number };
  // Set by the server once this author's missed-project-tag count crosses the
  // escalation threshold; the server owns that counter (recordProjectTagMiss).
  const projectTagWarning = res.headers.get("X-Relay-Project-Tag-Warning") ?? undefined;
  return { id: data.id, sequence: data.sequence, ...(projectTagWarning ? { projectTagWarning } : {}) };
}

// track:bulk-posts (#51257) — server cap on POST /api/messages/batch.
export const BATCH_MAX_MESSAGES = 100;

export interface BatchMessage {
  author: string;
  content: string;
  tags?: string[];
}

export interface BatchFailure {
  index: number;
  error: string;
}

export interface BatchResult {
  total: number;
  succeeded: number;
  failures: BatchFailure[];
}

/**
 * Posts messages through POST /api/messages/batch in chunks of
 * BATCH_MAX_MESSAGES (one rate-limit hit per chunk). Per-item failures are
 * reported by index into `messages`; a whole-request failure (auth, rate
 * limit, network) marks every item in that chunk as failed rather than
 * throwing, so callers can show "Changed N of M".
 */
export async function postBatch(
  messages: BatchMessage[],
  headers: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
): Promise<BatchResult> {
  const failures: BatchFailure[] = [];
  let succeeded = 0;
  for (let start = 0; start < messages.length; start += BATCH_MAX_MESSAGES) {
    const chunk = messages.slice(start, start + BATCH_MAX_MESSAGES);
    try {
      const res = await fetchImpl("/api/messages/batch", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ messages: chunk }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = body.error || `Batch post failed (HTTP ${res.status})`;
        chunk.forEach((_, i) => failures.push({ index: start + i, error }));
        continue;
      }
      for (const r of body.results ?? []) {
        if (r.ok) succeeded++;
        else failures.push({ index: start + r.index, error: String(r.message || r.error || "Post failed") });
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : "Batch post failed";
      chunk.forEach((_, i) => failures.push({ index: start + i, error }));
    }
  }
  return { total: messages.length, succeeded, failures };
}
